/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CSSProperties, useContext, useState} from "react";
import {tableCardStyle} from "../../home/AlertsTab";
import {TS_Table} from "../../../_TS_TABLE";
import {COLORS} from "@res/colors";
import {padding_rl_med} from "@styles/styles";
import {FONTS} from "@res/fonts";
import {SearchBar} from "../SearchBar";
import {PermissionDialog} from "../permissions/PermissionDialog";
import {AppRoleLevel, adminRoles, DB_Permission} from "@app/app-shared/permission";
import {ICONS} from "@res/icons";
import {PermissionsContext} from "./AdminTab";
import {OKButton} from "../../widget/OKButton";
import {AppPermissions} from "../../../utils/Permissions";
import {CheckinModule} from "@modules/CheckinModule";
import {ToastModule} from "@intuitionrobotics/thunderstorm/frontend";

type PermissionTableRow = {
    displayName: string
    actions: DB_Permission
}

export const AdminPermissionTab = () => {
    const [searchText, setSearchText] = useState("")
    const {permissions, upsertPermission} = useContext(PermissionsContext)
    const permissionContent = permissions
        .filter(p => p.name.toLowerCase().includes(searchText.toLowerCase()))
        .map(_permission => ({
            displayName: _permission.name,
            actions: _permission
        })).sort((a, b) => a.displayName.localeCompare(b.displayName));
    return <AppPermissions appRoles={adminRoles}>
        <div
            style={{overflow: "scroll", marginTop: "15px"}}
            className={"match_height"}
        >
            <div className={"ll_h_c"}>
                <SearchBar
                    value={searchText}
                    onValueChange={(value) => setSearchText(value)}
                    onClose={() => setSearchText('')}
                    placeholder={"Search by name"}
                />
                <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
                    <OKButton
                        enabled
                        onClick={() => PermissionDialog.show(permissions, upsertPermission)}
                        text={"Add Permission"}
                        wrapperStyle={{
                            minHeight: "unset",
                            minWidth: "unset"
                        }}
                        inverted
                    />
                </AppPermissions>
                <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
                    <OKButton
                        enabled
                        onClick={() => CheckinModule.triggerDaily(ok => {
                            ok
                                ? ToastModule.toastInfo("Daily check-in cron triggered")
                                : ToastModule.toastError("Failed to trigger daily check-in cron");
                        })}
                        text={"Trigger Daily Check-in"}
                        wrapperStyle={{
                            minHeight: "unset",
                            minWidth: "unset",
                            marginLeft: 8
                        }}
                        inverted
                    />
                </AppPermissions>
            </div>
            <div style={{...tableCardStyle, minHeight: "80%", minWidth: "80%"}}>
                <TS_Table<PermissionTableRow>
                    id={"admin-table"}
                    header={["displayName", "actions"]}
                    rows={permissionContent}
                    tr={() => ({
                        className: "hover-background"
                    })}
                    td={{
                        style: {borderBottom: `1px solid ${COLORS.grays_2}`}
                    }}
                    style={{width: "100%"}}
                    headerRenderer={{
                        displayName: () => <Header title={"Display Name"}/>,
                        actions: () => <Header title={"Actions"} style={{justifyContent: "center"}}/>
                    }}
                    cellRenderer={{
                        actions: (permission: DB_Permission) => {
                            return <AppPermissions appRoles={[AppRoleLevel.SuperAdmin]}>
                                <div
                                    key={"edit"}
                                    className={"ll_h_c"}
                                    onClick={() => PermissionDialog.show(permissions, upsertPermission, permission)}
                                    style={{cursor: "pointer", justifyContent: "space-around"}}
                                >
                                    {ICONS.icon_filters(COLORS.grays_dark, 20)}
                                </div>
                            </AppPermissions>
                        },
                        displayName: (cellValue: string) => {
                            // TODO: address following comment
                            // line height should be at least half of row height for maximum of 2 lines
                            const textStyle = {color: COLORS.neutral_text_dark, lineHeight: "24px"};
                            return <div className={padding_rl_med}>{FONTS.reg_16(cellValue, textStyle)}</div>
                        }
                    }}
                />
            </div>
        </div>
    </AppPermissions>
}

const Header = ({title, style}: { title?: string, style?: CSSProperties }) => {
    return <div className={`ll_h_c ${padding_rl_med}`}
                style={{...style, height: 34, backgroundColor: COLORS.color_gray_medium}}>
        {title && FONTS.reg_14_secondary_dark(title, {whiteSpace: "nowrap"})}
    </div>
}
